import { useState, useRef, useEffect } from "react";
import {
    Form,
    Link,
    useNavigate,
    useRouteLoaderData,
    useSearchParams,
} from "react-router-dom";
import { bidTypeOptions, categoryOptions, Opportunity, stateOptions } from "./types";
import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import { myAxios } from "../common/functions";
export default function OpportunityForm({
    keyword
}: {
    keyword: "Add" | "Edit";
}) {
    const loaderOrg =
    useRouteLoaderData("opportunity") ?? {
        opportunityName: "", 
        organizationName: "", 
        organizationId: null, 
        contacts: [], 
        userResponsible: [], 
        bidAmount: "",
        bidType: "",
        category: "",
        state: "",
        location: "", 
        actualCloseDate: "",
        background: "",  
    };
    const [formValues, setFormValues] = useState<Opportunity>(loaderOrg);
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormValues((prev)=> ({
            ...prev,
            [name]: value
        }));
    };
    const formRef = useRef(null);
    const organizationIdParam = useSearchParams()[0].get("organizationId");
    const navigate = useNavigate();
    useEffect(() => {
        const controller = new AbortController();
        let isMouted = true;
        const getData = async () => {
            if(keyword === "Add" && organizationIdParam){
                const formData = new FormData();
                formData.append("id", organizationIdParam ?? "");
                const res = await myAxios({ fileName: "opportunities/getOrganizationName.php", formData, signal: controller.signal });
                if(!isMouted){
                    return;
                }
                if(!res || !res.data){
                     navigate(`/Opportunities/Add`, { replace: true });
                     return;
                }
                setFormValues((formValues) => ({...formValues, organizationName: res.data.name, organizationId: Number(organizationIdParam)}));
            }
        }
        getData();
        return () => {
            isMouted = false;
            controller.abort();
        };
    }, [keyword, organizationIdParam, navigate]);
    return (
        <Form method="post" ref = {formRef}>
            <input type="hidden" name="action" value={keyword} />
            <input type="hidden" name="id" value={formValues.id ?? ""} />
            <div className="cssSeperateWhenWide ps-3 mb-2">
                <div className="flex-grow-1 me-3">
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="opportunityName">
                            Opportunity Name:
                        </label>
                        <input
                            required
                            className="form-control flex-grow-1 ms-3"
                            id="opportunityName"
                            name="opportunityName"
                            value={formValues.opportunityName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="organizationId">
                            Organization:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search organization…"
                            required = {true}
                            name = {"organizationId"}
                            inputId = {"organizationId"}
                            readOnly = {!!organizationIdParam}
                            fileName = "common/searchOrganization.php"
                            value = {formValues.organizationId !== null ? {label: formValues.organizationName!, value: String(formValues.organizationId ?? "")} : null}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    organizationId: !next ? null : Number((next as SelectOption)?.value),
                                    organizationName: (next as SelectOption)?.label ?? null, 
                                    contacts: [] // Clear contacts when organization changes
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="contactIds">
                            Contacts:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search contacts…"
                            required = {true}
                            name = {"contactIds"}
                            inputId = {"contactIds"}
                            fileName = "common/searchContactInOrganization.php"
                            value = {formValues.contacts}
                            isMulti = {true}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    contacts: next as SelectOption[]
                                })
                            }
                            formValues={{ organizationId: formValues.organizationId ?? "" }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="userIds">
                            User Responsibles:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search users…"
                            required = {true}
                            name = {"userIds"}
                            inputId = {"userIds"}
                            fileName = "common/searchUser.php"
                            value = {formValues.users}
                            isMulti = {true}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    users: next as SelectOption[]
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="role">Probability:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            type = "number"
                            id="probability"
                            name="probability"
                            value={formValues.probability}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="bidType">Bid Type:</label>
                        <select className="form-control ms-3" id = "bidType" name = "bidType" value = {formValues.bidType} onChange={handleChange}>
                            <option value = "">Select</option>
                            {bidTypeOptions.map(v => <option key = {v}>{v}</option>)}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="bidAmount">Bid Amount:</label>
                         <input
                            className="form-control flex-grow-1 ms-3"
                            type = "number"
                            id="bidAmount"
                            name="bidAmount"
                            value={formValues.bidAmount}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="category">Category:</label>
                        <select className="form-control ms-3" id = "category" name = "category" value = {formValues.category} onChange={handleChange}>
                            <option value = "">Select</option>
                            {categoryOptions.map(v => <option key = {v}>{v}</option>)}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="state">State:</label>
                        <select className="form-control ms-3" id = "state" name = "state" value = {formValues.state} onChange={handleChange}>
                            <option value = "">Select</option>
                            {stateOptions.map(v => <option key = {v}>{v}</option>)}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="location">Location:</label>
                            <input
                            className="form-control flex-grow-1 ms-3"
                            type = "text"
                            id="location"
                            name="location"
                            value={formValues.location}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="actualCloseDate">Actual Close Date:</label>
                            <input
                            className="form-control flex-grow-1 ms-3"
                            type = "date"
                            id="actualCloseDate"
                            name="actualCloseDate"
                            value={formValues.actualCloseDate}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="d-flex flex-column flex-grow-1 align-items-start mt-2">
                    <label className="fw-bold" htmlFor="background">Background:</label>
                    <textarea
                        className="form-control w-100 flex-grow-1"
                        id="background"
                        name="background"
                        value={formValues.background}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div className="my-3">
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {keyword}
                </button>
                <Link
                    to={organizationIdParam?`/Organizations/${organizationIdParam}`:".."}
                    className="btn btn-outline-secondary float-end"
                >
                    Back
                </Link>
            </div>
        </Form>
    );
}
